/**
 * Sparkline — minimal SVG line + area for inline metric trends.
 *
 * Phase C (system-sync) — premium glance affordance для metric cards
 * which currently show only current + delta. Stat cards adopt this
 * below the value display.
 *
 * Visual canon:
 *   - 1.4px polyline (canon section 6 ink-1)
 *   - Optional area fill 8% accent (matches CanonChart)
 *   - Optional latest-point dot (4px, canon iris-l accent)
 *   - Tone-aware stroke via `stroke` prop (defaults to fg-primary)
 *
 * Empty-state when values.length < 2 — returns null (parent shows
 * placeholder or hides).
 *
 * Pure: no hooks, no state, no i18n. Re-renders only when values
 * reference changes.
 */
import { useMemo } from "react";

export interface SparklineProps {
  values: number[];
  /** Цвет линии. Любое CSS-значение цвета, включая `var(--token)`. */
  stroke?: string;
  /** When set, paints area below line с this color и 8% opacity. */
  fill?: string;
  /** When true, shows a 3px dot at the last value. */
  showLatestDot?: boolean;
  /** Default 80×24 — fits nicely above stat delta line. */
  width?: number;
  height?: number;
  /**
   * Базовый уровень метрики: HRV-база, привычный пульс покоя, целевой сон.
   *
   * Без него тренд отвечает «росло или падало» и молчит о главном — где
   * сейчас относительно нормы. Плитка писала «база 57» словами, но на линии
   * этой отметки не было, и увидеть, что сегодня ниже коридора, было нечем.
   *
   * База ВХОДИТ в масштаб: иначе, когда все значения выше неё, она оказалась
   * бы за нижним краем и её бы просто не нарисовали — молча.
   */
  baseline?: number;
  /** Test pinning. */
  testId?: string;
  ariaLabel?: string;
  className?: string;
}

export function Sparkline({
  values,
  stroke = "var(--fg-primary)",
  fill,
  showLatestDot = false,
  width = 80,
  height = 24,
  baseline,
  testId,
  ariaLabel,
  className = "",
}: SparklineProps) {
  const path = useMemo(() => {
    if (values.length < 2) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const v of values) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (!isFinite(min) || !isFinite(max)) return null;
    // База участвует в масштабе наравне со значениями — иначе она уедет за
    // край поля ровно в том случае, ради которого нужна: когда ряд целиком
    // выше или целиком ниже нормы.
    if (baseline != null && isFinite(baseline)) {
      if (baseline < min) min = baseline;
      if (baseline > max) max = baseline;
    }
    // Defensive guard: flat line (min === max) — paint mid line.
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const n = values.length;
    const stepX = width / (n - 1);
    const span = max - min;
    const points: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < n; i++) {
      const x = i * stepX;
      const y = height - ((values[i] - min) / span) * height;
      points.push({ x, y });
    }
    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(" ");
    const areaPath =
      `${linePath} L ${points[n - 1].x.toFixed(1)} ${height} L ${points[0].x.toFixed(1)} ${height} Z`;
    const last = points[n - 1];
    const baseY =
      baseline != null && isFinite(baseline)
        ? height - ((baseline - min) / span) * height
        : null;
    return { linePath, areaPath, last, baseY };
  }, [values, width, height, baseline]);

  if (!path) return null;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={ariaLabel ?? "metric trend"}
      data-testid={testId ?? "sparkline"}
      className={`block ${className}`}
      preserveAspectRatio="none"
    >
      {fill && (
        <path
          d={path.areaPath}
          fill={fill}
          opacity={0.08}
          data-testid={`${testId ?? "sparkline"}-area`}
        />
      )}
      {path.baseY != null && (
        <line
          x1={0}
          x2={width}
          y1={path.baseY}
          y2={path.baseY}
          stroke="var(--fg-muted)"
          strokeWidth={1}
          strokeDasharray="2 2"
          opacity={0.7}
          data-testid={`${testId ?? "sparkline"}-baseline`}
        />
      )}
      <path
        d={path.linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        data-testid={`${testId ?? "sparkline"}-line`}
      />
      {showLatestDot && (
        <circle
          cx={path.last.x}
          cy={path.last.y}
          r={3}
          fill={stroke}
          data-testid={`${testId ?? "sparkline"}-dot`}
        />
      )}
    </svg>
  );
}
